import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Wifi } from "lucide-react";
import { fmtDateTime, rupiah } from "../lib/api";

const jam = (iso) => (iso ? fmtDateTime(iso) : "-");

const MacCell = ({ r }) => r.shared ? (
  <span className="inline-flex items-center gap-1" title={`MAC berganti-ganti (${r.macs.length} perangkat): ${r.macs.join(", ")}`} data-testid={`vt-shared-${r.name}`}>
    <span className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30 text-[10px] font-bold normal-case">⚠ {r.macs.length} HP</span>
    <span>{r.mac}</span>
  </span>
) : (r.mac || <span className="text-muted-foreground normal-case">belum</span>);

function RouterBlock({ router, groups, qty, nilai, shared, open, onToggle }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid={`vt-cabang-${router}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-emerald-500/10 to-transparent hover:from-emerald-500/20 transition-colors text-left">
        <div className="flex items-center gap-2 min-w-0">
          <Wifi className="w-4 h-4 text-emerald-500 shrink-0" />
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nama Router</div><div className="font-heading font-bold text-sm truncate">{router}</div></div>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs font-mono">
          <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"><b>{qty}</b> login</span>
          {shared > 0 && <span className="px-2 py-0.5 rounded-md bg-rose-500/15 text-rose-400 border border-rose-500/30" title="Kode yang dipakai bergantian di beberapa HP">⚠ <b>{shared}</b> bergantian</span>}
          <span className="px-2 py-0.5 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/30">{rupiah(nilai)}</span>
          <span className="hidden sm:inline text-muted-foreground">{groups.length} paket</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-border">
          {groups.map((g) => (
            <div key={g.profile} className="px-4 py-3" data-testid={`vt-paket-${router}-${g.profile}`}>
              <div className="flex items-center justify-between mb-2 text-xs">
                <div><span className="text-muted-foreground">Nama Paket: </span><span className="font-bold text-[#F04E23]">{g.profile || "-"}</span></div>
                <div className="font-mono text-muted-foreground">{g.rows.length} kode{g.nilai ? ` • ${rupiah(g.nilai)}` : ""}</div>
              </div>
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-accent/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr><th className="text-left px-3 py-1.5 w-10">No</th><th className="text-left px-3 py-1.5">Username / Kode</th><th className="text-left px-3 py-1.5">Terpakai (Login Pertama)</th><th className="text-left px-3 py-1.5">Mac Address</th><th className="text-right px-3 py-1.5">Harga</th><th className="text-left px-3 py-1.5">Dicetak Oleh</th></tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r, i) => (
                      <tr key={r.name} className={`border-t border-border/60 odd:bg-background/40 hover:bg-emerald-500/5 ${r.shared ? "bg-rose-500/5" : ""}`}>
                        <td className="px-3 py-1.5 font-mono text-muted-foreground">{i + 1}</td>
                        <td className="px-3 py-1.5 font-mono font-bold tracking-wider text-sm">{r.name}</td>
                        <td className="px-3 py-1.5 font-mono text-emerald-400">{jam(r.used_at)}{r.source === "scan" && <span className="ml-1 text-[9px] text-muted-foreground">±30m</span>}</td>
                        <td className="px-3 py-1.5 font-mono text-sky-400 uppercase"><MacCell r={r} /></td>
                        <td className="px-3 py-1.5 font-mono text-right">{r.jual ? rupiah(r.jual) : "-"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.created_by || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const MacCellRow = MacCell;
export function VoucherTerpakaiPerCabang({ rows }) {
  const [open, setOpen] = useState({});
  const [all, setAll] = useState(false);
  const blocks = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const k = r.router_name || "-";
      if (!m.has(k)) m.set(k, new Map());
      const pm = m.get(k);
      if (!pm.has(r.profile || "-")) pm.set(r.profile || "-", []);
      pm.get(r.profile || "-").push(r);
    });
    return [...m.entries()].map(([router, pm]) => {
      const groups = [...pm.entries()].map(([profile, rs]) => ({ profile, rows: rs.sort((a, b) => (a.used_at || "").localeCompare(b.used_at || "")), nilai: rs.reduce((s, x) => s + (x.jual || 0), 0) })).sort((a, b) => b.rows.length - a.rows.length);
      const qty = groups.reduce((s, g) => s + g.rows.length, 0);
      return { router, groups, qty, nilai: groups.reduce((s, g) => s + g.nilai, 0), shared: groups.reduce((s, g) => s + g.rows.filter((x) => x.shared).length, 0) };
    }).sort((a, b) => b.qty - a.qty);
  }, [rows]);
  if (!blocks.length) return null;
  return (
    <div className="space-y-2" data-testid="vt-per-cabang">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-heading font-semibold flex items-center gap-2"><Wifi className="w-4 h-4 text-[#F04E23]" /> Per Cabang <span className="text-xs font-normal text-muted-foreground">({blocks.length} cabang — klik untuk rincian per paket)</span></h2>
        <button type="button" onClick={() => { setAll(!all); setOpen({}); }} className="text-xs text-sky-400 hover:underline" data-testid="vt-toggle-all">{all ? "Tutup semua" : "Buka semua"}</button>
      </div>
      {blocks.map((b) => <RouterBlock key={b.router} {...b} open={open[b.router] ?? all} onToggle={() => setOpen({ ...open, [b.router]: !(open[b.router] ?? all) })} />)}
    </div>
  );
}
